import { useEffect, useRef, useState } from "react";
import { fetchVenue, mapsLink, saveVenue, type VenueDetail } from "../../lib/rosterPage";
import { fShort, plural } from "../../lib/format";
import { useApp } from "../appState";
import { Sheet } from "../Sheet";
import { Timeline } from "../Timeline";

export function VenueSheet({ id }: { id: number }) {
  const { open, close, version, changed, firstYear } = useApp();
  const [v, setV] = useState<VenueDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setFormState] = useState({ name: "", location: "" });
  // Any write in the app refetches this sheet; an unsaved edit survives it. (2026-09-11 gate)
  const dirty = useRef(false);
  const setForm = (f: { name: string; location: string }) => {
    dirty.current = true;
    setFormState(f);
  };

  useEffect(() => {
    let live = true;
    fetchVenue(id)
      .then((d) => {
        if (!live) return;
        setV(d);
        if (!dirty.current) setFormState({ name: d.name, location: d.location ?? "" });
      })
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [id, version]);

  const fail = (e: Error) => setError(e.message);
  return (
    <Sheet title={v?.name ?? ""} label={v?.name ?? "Venue"} onClose={close}>
      {error && <p className="error">{error}</p>}
      {!v && !error && <p className="loading">Loading…</p>}
      {v && (
        <>
          <div className="muted" style={{ marginTop: -10 }}>
            {v.location}
            {v.closed && <> · <span className="chip closed">closed</span></>} · <span className="mono">{plural(v.shows.length, "show")}</span>
          </div>
          <Timeline years={v.years} first={firstYear} big labels />
          <div className="sheet-acts">
            <a className="btn ghost sm" href={mapsLink(v.name, v.location, v.place_id)} target="_blank" rel="noopener noreferrer">Open in Maps</a>
            <button type="button" className="btn ghost sm" onClick={() => saveVenue(v.id, v.name, v.location ?? "", !v.closed).then(() => changed(v.closed ? "Marked open" : "Marked closed"), fail)}>
              {v.closed ? "Mark open" : "Mark closed"}
            </button>
          </div>
          <section>
            <p className="eyebrow" style={{ marginBottom: 4 }}>Shows</p>
            <div className="hist">
              {v.shows.map((s) => (
                <button type="button" key={s.id} onClick={() => open("show", s.id)}>
                  <span className="hd">{fShort(s.date)}</span>
                  <span>{s.bill}</span>
                </button>
              ))}
            </div>
          </section>
          <div className="form">
            <label className="f"><span>Name</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="f"><span>Location</span><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></label>
            <button type="button" className="btn" style={{ justifySelf: "start" }} disabled={!form.name.trim()} onClick={() => saveVenue(v.id, form.name, form.location, v.closed).then(() => { dirty.current = false; changed("Venue saved"); }, fail)}>Save</button>
          </div>
        </>
      )}
    </Sheet>
  );
}
